import { Component, AfterViewInit, EventEmitter, ElementRef, forwardRef, Input, Output, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { take } from 'rxjs/operators';

import { DateUtilsService } from '../../../core/services/utils/date';
import { Periodicidade, periodicidadeDiaria, isPeriodicidadeDiaria, periodicidadeMensal, isPeriodicidadeMensal, periodicidadeTrimestral, isPeriodicidadeTrimestral, periodicidadeSemestral, isPeriodicidadeSemestral, periodicidadeAnual, isPeriodicidadeAnual, periodicidadePeriodo, isPeriodicidadePeriodo, periodicidadeTodoPeriodo, isPeriodicidadeTodoPeriodo } from '../../periodicidade';
import { getDatepickeModeOptions, datepickerModeDay, isDatepickerModeDay, isDatepickerModeMonthAndYear, isDatepickerModeYear, isDatepickerModePeriodicidade, isDatepickerModeCustom } from './datepicker-mode';
import { Feriado } from '../../../home/feriado/feriado';
import { FeriadoFilter } from '../../../home/feriado/filter/feriado-filter';
import { FeriadoCrudHttpService } from '../../../home/feriado/services/feriado-crud-http.service';

/** Component para utilização de campos do tipo data com base na biblioteca bootstrap-datepicker
 * Mais informações: https://bootstrap-datepicker.readthedocs.io/en/latest/index.html
 */
@Component({
    selector: 'sgt-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.less'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatepickerComponent), multi: true }
    ]
})
export class DatepickerComponent implements OnInit, AfterViewInit, ControlValueAccessor, OnDestroy {
    @Input() ngModel: Date;
    @Output() ngModelChange = new EventEmitter<Date>();

    @Input() startDate: Date;
    @Output() startDateChange = new EventEmitter<Date>();
    @Input() endDate: Date;
    @Output() endDateChange = new EventEmitter<Date>();

    /** Mode de exibição do datepicker */
    @Input() mode: number = datepickerModeDay.id;

    // Options
    private options: any;
    /** Opções personalizadas para o datepicker. Informar quando for o modo Custom */
    @Input() customOptions: any;
    /** Classes CSS personalizadas para o datepicker */
    @Input() containerClasses = '';
    /** Se deve exibir o botão que abre/feche o calendário do datepicker */
    @Input() showBtnCalendar = true;
    /** Se o datepicker posui navegação de data anterior e posterior */
    @Input() withNavigation = false;
    /** Texto para ser exibido com um label no input group */
    @Input() inputPrependText: string = null;
    @Input() required: any;
    @Input() disabled: any;
    @Input() readonly: any;
    @Input() ngControl: NgControl;
    @Input() autofocus = false;
    @Output() change = new EventEmitter<Date>();

    // Periodicidade
    /** Se pode editar a periodicidade */
    @Input() canEditPeriodicidade = true;
    /** Periodicidade do datepicker */
    @Input() periodicidade: number = periodicidadeDiaria.id;
    /** Ao alterar a periodicidade no datepicker */
    @Output() periodicidadeChange = new EventEmitter<number>();
    /** Se deve exibir a periodicidade diária */
    @Input() showPeriodicidadeDiaria = false;
    /** Se deve exibir a periodicidade Mês */
    @Input() showPeriodicidadeMensal = false;
    /** Se deve exibir a periodicidade Trimestre */
    @Input() showPeriodicidadeTrimestral = false;
    /** Se deve exibir a periodicidade Semestre */
    @Input() showPeriodicidadeSemestral = false;
    /** Se deve exibir a periodicidade Ano */
    @Input() showPeriodicidadeAnual = false;
    /** Se deve exibir a periodicidade Período */
    @Input() showPeriodicidadePeriodo = false;
    /** Se deve exibir a periodicidade Período */
    @Input() showPeriodicidadeTodoPeriodo = false;
    /** Periodicidades do datepicker */
    periodicidades: Periodicidade[];

    @Input() dia: number;
    @Output() diaChange = new EventEmitter<number>();
    @Input() mes: number;
    @Output() mesChange = new EventEmitter<number>();
    @Input() trimestre: number;
    @Output() trimestreChange = new EventEmitter<number>();
    @Input() semestre: number;
    @Output() semestreChange = new EventEmitter<number>();
    @Input() ano: number;
    @Output() anoChange = new EventEmitter<number>();

    /** Se deve desabilitar o sábado */
    @Input() disableSaturday = false;
    /** Se deve desabilitar o domingo */
    @Input() disableSunday = false;
    /** Datas que devem ser desabilitadas */
    @Input() disableDates: Date[] = [];
    /** Se deve desabilitar feriados */
    @Input() disableFeriados = false;

    /** Data desabilitadas */
    private datesDisabled: Date[] = [];

    @ViewChild('inputDate') inputDate: ElementRef;
    private isInputValueChanged = false; // Se o valor no input do datepicker foi alterado
    datepicker: any;
    /** Variavel de controle para identificar se a mudanção da data no datepicker está sendo feito pelo método write que significa que é uma escrita externa a mudanças de datas feitas no datepicker */
    private isChangingByWrite = false;
    /** Variavel de controle para verificar se deve ou não desabilitar as datas no datepicker */
    private isExecSetDatesDisabled = true;
    propagateChange: any = () => {};

    constructor(private datePipe: DatePipe,
                private dateUtilsService: DateUtilsService,
                private feriadoCrudHttpService: FeriadoCrudHttpService) { }

    ngOnInit() {
        this.createPeriodicidades();
    }

    ngAfterViewInit() {
        this.create();

        if (this.autofocus) {
            this.setFocus();
        }
    }

    /** Verifica se o modo de exibição é por dia */
    isModeDay(): boolean {
        return isDatepickerModeDay(this.mode);
    }

    /** Verifica se o modo de exibição é por mês e ano */
    isModeMonthAndYear(): boolean {
        return isDatepickerModeMonthAndYear(this.mode);
    }

    /** Verifica se o modo de exibição é por ano */
    isModeYear(): boolean {
        return isDatepickerModeYear(this.mode);
    }

    /** Verifica se o modo de exibição é pela periodicidade */
    isModePeriodicidade(): boolean {
        return isDatepickerModePeriodicidade(this.mode);
    }

    /** Verifica se o modo de exibição é customizado */
    isModeCustom(): boolean {
        return isDatepickerModeCustom(this.mode);
    }

    /** Verifica se a periodicidade selecionada é a Diária */
    isPeriodicidadeDiaria(): boolean {
        return isPeriodicidadeDiaria(this.periodicidade);
    }

    /** Verifica se a periodicidade selecionada é a Mensal */
    isPeriodicidadeMensal(): boolean {
        return isPeriodicidadeMensal(this.periodicidade);
    }

    /** Verifica se a periodicidade selecionada é a Trimestral */
    isPeriodicidadeTrimestral(): boolean {
        return isPeriodicidadeTrimestral(this.periodicidade);
    }

    /** Verifica se a periodicidade selecionada é a Semestral */
    isPeriodicidadeSemestral(): boolean {
        return isPeriodicidadeSemestral(this.periodicidade);
    }

    /** Verifica se a periodicidade selecionada é a Anual */
    isPeriodicidadeAnual(): boolean {
        return isPeriodicidadeAnual(this.periodicidade);
    }

    /** Verifica se a periodicidade selecionada é a Período */
    isPeriodicidadePeriodo(): boolean {
        return isPeriodicidadePeriodo(this.periodicidade);
    }

    /** Verifica se a periodicidade selecionada é todo período */
    isPeriodicidadeTodoPeriodo(): boolean {
        return isPeriodicidadeTodoPeriodo(this.periodicidade);
    }


    /** Cria e adiciona as periodicidades disponíveis para seleção no datepicker */
    private createPeriodicidades() {
        this.periodicidades = [];
        // Diária
        if (this.showPeriodicidadeDiaria) {
            this.periodicidades.push(periodicidadeDiaria);
        }
        // Mensal
        if (this.showPeriodicidadeMensal) {
            this.periodicidades.push(periodicidadeMensal);
        }
        // Trimestral
        if (this.showPeriodicidadeTrimestral) {
            this.periodicidades.push(periodicidadeTrimestral);
        }
        // Semestral
        if (this.showPeriodicidadeSemestral) {
            this.periodicidades.push(periodicidadeSemestral);
        }
        // Anual
        if (this.showPeriodicidadeAnual) {
            this.periodicidades.push(periodicidadeAnual);
        }
        // Período
        if (this.showPeriodicidadePeriodo) {
            this.periodicidades.push(periodicidadePeriodo);
        }
        // Todo Período
        if (this.showPeriodicidadeTodoPeriodo) {
            this.periodicidades.push(periodicidadeTodoPeriodo);
        }
    }

    /** Cria o datepicker utilizando a biblioteca bootstrap-datepicker */
    private create() {
        if (!this.inputDate) {
            return;
        }

        /** Setando as options do datepicker conforme o modo de exibição */
        if (this.isModeCustom()) { // Customizado => Ontem através do @Input customOptions
            this.options = this.customOptions;
        } else { // Obtem através do modo informado
            this.options = getDatepickeModeOptions(this.mode, this.periodicidade);
        }

        const el = jQuery(this.inputDate.nativeElement) as any;

        // Adiconando a máscara da data e placeholder
        el.mask(this.options.mask);
        this.inputDate.nativeElement.placeholder = this.options.placeholder;

        // Inicializando o bootstrap-datepicker
        this.datepicker = el.datepicker(this.options)
                            .on('changeDate', (e: any) => this.onChangeDate(e))
                            .on('changeMonth', (e) => this.onChangeMonth(e))
                            .on('show.bs.modal', (e: any) => {
                                e.stopPropagation();
                            })
                            .on('shown.bs.modal', (e: any) => {
                                e.stopPropagation();
                            })
                            .on('hide.bs.modal', (e: any) => {
                                e.stopPropagation();
                            })
                            .on('hidden.bs.modal', (e: any) => {
                                e.stopPropagation();
                            });

        // Desabilitando datas no datepicker conforme configurações iniciais
        this.setDatesDisabled(new Date());
    }

    /** Seta a periodicidade do datepicker e atualiza as respectivas opções */
    setPeriodicidade(periodicidade: Periodicidade, newDate: Date = null, isExecSetDatesDisabled = false): Promise<void> {
        return new Promise<void>((resolve) => {
            this.isExecSetDatesDisabled = isExecSetDatesDisabled;
            // Setando a periodicidade informada
            this.periodicidade = periodicidade.id;

            setTimeout(() => {
                if (!this.isPeriodicidadeTodoPeriodo()) {
                    // Removendo o datepicker atual
                    this.destroy();
                    // Criando novamente o datepicker utilizando as novas opções conforme modo informado
                    this.create();

                    // Setando a data no datepicker e chamando método update para atualizar a data no datepicker após alterar o modo
                    this.datepicker?.datepicker('setDate', newDate ? newDate : this.ngModel);
                    this.datepicker?.datepicker('update');
                }

                resolve();
            }, 0);
        });
    }

    /** Ao alterar a periodicidade do datepicker */
    onChangePeriodicidade(periodicidade: number, isFromPeriodo = false) {
        // Setando a periodicidade informada
        this.periodicidade = periodicidade;
        this.periodicidadeChange.emit(this.periodicidade);

        // Se o modo for pela periodicidade então criamos novamente o datepicker para aplicar as opções conforme a periodicidade selecionada
        setTimeout(() => {
            if (this.isModePeriodicidade() || isFromPeriodo) {
                // Removendo o datepicker atual
                this.destroy();
                // Criando novamente o datepicker utilizando as novas opções conforme modo informado
                this.create();

                // Setando a data no datepicker e chamando método update para atualizar a data no datepicker após alterar o modo
                this.datepicker?.datepicker('setDate', new Date(this.ngModel));
                this.datepicker?.datepicker('update');
            }
        }, 0);
    }

    /** Function que detecta quando é alterada a data no bootstrap-datepicker */
    private async onChangeDate(e: any) {
        const date = e.date as Date;

        // Desabilitando as datas com base no dia, mês e ano da data selecioanda
        if (this.isExecSetDatesDisabled) {
            await this.setDatesDisabled(date);
        } else {
            this.isExecSetDatesDisabled = true; // Setando para true para na próxima interação executar o método setDatesDisabled
        }

        // Confirmando a seleção da data
        this.confirmDateChange(date);
    }

    /** Ao alterar o mês no datepicker */
    private async onChangeMonth(e: any) {
        const date = e.date as Date;
        await this.setDatesDisabled(date);
    }

    /** Confirma a seleção da data */
    private confirmDateChange(date: Date) {
        // Verificando se a data selecionada é uma data desabilitada
        const isDisabled = this.isDateDisabled(date);
        if (isDisabled && !this.isChangingByWrite) { // Se for uma data desabilitada e a mesma não foi adiconada através do método write (externo ao datepicker)...
            // Se a data desabilitada for diferente da atual então voltamos para a data atual fazendo que a data que foi selecionada seja ignorada
            if (!this.ngModel || (date?.getTime() !== this.ngModel.getTime())) {
                this.datepicker?.datepicker('setDate', this.ngModel);
                this.datepicker?.datepicker('update');
                return;
            }
        }

        // Setandos os dados da data
        this.setDia(date);
        this.setMes(date);
        this.setTrimestre(date);
        this.setSemestre(date);
        this.setAno(date);

        this.propagateChange(date);
        this.change.emit(date);

        this.isChangingByWrite = false;
    }

    /** Ao alterar o trimestre */
    onChangeTrimestre(trimestre: number, ano: number) {
        let date: Date;
        switch (trimestre) {
            case 1:
                date = new Date(ano, 0, 1);
                break;
            case 2:
                date = new Date(ano, 3, 1);
                break;
            case 3:
                date = new Date(ano, 6, 1);
                break;
            case 4:
                date = new Date(ano, 9, 1);
                break;
            default:
                break;
        }

        this.datepicker?.datepicker('setDate', date);
        this.datepicker?.datepicker('update');
    }

    /** Ao alterar o semestre */
    onChangeSemestre(semestre: number, ano: number) {
        let date: Date;
        switch (semestre) {
            case 1:
                date = new Date(ano, 0, 1);
                break;
            case 2:
                date = new Date(ano, 6, 1);
                break;
            default:
                break;
        }

        this.datepicker?.datepicker('setDate', date);
        this.datepicker?.datepicker('update');
    }


    /** Seta o dia através da data selecionada */
    private setDia(date: Date) {
        this.dia = date ? date.getDate() : null;
        this.diaChange.emit(this.dia);
    }

    /** Seta o mês através da data selecionada */
    private setMes(date: Date) {
        this.mes = date ? date.getMonth() + 1 : null;
        this.mesChange.emit(this.mes);
    }

    /** Seta o trimestre através da data selecionada */
    private setTrimestre(date: Date) {
        this.trimestre = this.dateUtilsService.getQuarter(date);
        this.trimestreChange.emit(this.trimestre);
    }

    /** Seta o semestre através da data selecionada */
    private setSemestre(date: Date) {
        this.semestre = this.dateUtilsService.getHalfYear(date);
        this.semestreChange.emit(this.semestre);
    }

    /** Seta o ano através da data selecionada */
    private setAno(date: Date) {
        this.ano = date ? date.getFullYear() : null;
        this.anoChange.emit(this.ano);
    }

    /** Seta o foco no input do datepicker */
    setFocus() {
        if (!this.inputDate) {
            return;
        }

        jQuery(this.inputDate.nativeElement).trigger('focus');
    }

    /** Exibe o calendário do datepicker */
    show() {
        this.datepicker?.datepicker('show');
    }

    onKeyup(event: KeyboardEvent) {
        // Teclas que devem ser ignoradas
        const ignoresKeys: string[] = ['Tab', 'Enter', 'Shift', 'Control', 'Alt', 'Pause', 'CapsLock', 'Escape', ' ', 'PageUp', 'PageDown', 'End', 'Home', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Insert', 'Meta',
                                         'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12', 'NumLock', 'ScrollLock', 'Dead', '!', '@', '#', '$', '%', '&', '*', '(', ')', '-', '_', '+', '=', '[', ']',
                                         '{', '}', '|', '\'', ';', ':', '"', `'`, ',', '.', '<', '>', '?'];
        if (ignoresKeys.includes(event.key)) {
            return;
        }

        this.isInputValueChanged = true;
    }

    onBlur(value: string) {
        // Se o valor do input for vazio, então setamos o valor do ngModel para null
        if (!value || value.trim().length === 0) {
            this.setDia(null);
            this.setMes(null);
            this.setTrimestre(null);
            this.setSemestre(null);
            this.setAno(null);

            this.propagateChange(null);
            this.inputDate.nativeElement.value = '';
        } else if (this.isInputValueChanged) { // Se não, se o valor no input foi alterado, setamos a data informada no input
            this.datepicker?.datepicker('setDate', value);
            this.datepicker?.datepicker('update');
        }

        this.isInputValueChanged = false;
    }

    /** Quando recebe um valor */
    writeValue(value) {
        if (!this.inputDate) {
            return;
        }

        if (value) {
            this.isChangingByWrite = true;
            // this.datepicker?.datepicker('setDate', new Date(value));
            this.datepicker?.datepicker('update', new Date(value));
            this.propagateChange( new Date(value));
        } else {
            this.inputDate.nativeElement.value = '';
        }
      }

    /** Propaga as alterações realizadas no datepicker */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() {}

    /** Seta a data inicial no Datepicker não permitindo selecionar uma data anterior a informada */
    setStartDate(date: Date) {
        if (!date) {
            return;
        }
        this.datepicker?.datepicker('setStartDate', this.datePipe.transform(date, 'dd/MM/yyyy'));
    }

    /** Seta a data final no Datepicker não permitindo selecionar uma data posterior a informada */
    setEndDate(date: Date) {
        if (!date) {
            return;
        }
        this.datepicker?.datepicker('setEndDate', this.datePipe.transform(date, 'dd/MM/yyyy'));
    }

    /** Título do retorno da data com base no mode do datepicker */
    priorTitle(): string {
        if (!this.withNavigation || !this.ngModel) {
            return '';
        }

        if (this.isPeriodicidadeMensal()) { // Mensal
            return 'Mês Anterior';
        } else if (this.isPeriodicidadeTrimestral()) { // Trimestral
            return 'Trimestre Anterior';
        } else if (this.isPeriodicidadeSemestral()) { // Semestral
            return 'Semestre Anterior';
        } else if (this.isPeriodicidadeAnual()) { // Anual
            return 'Ano Anterior';
        } else { // Diária
            return 'Dia Anterior';
        }
    }

    /** Retorna a data com base no mode do datepicker */
    prior() {
        if (!this.withNavigation || !this.ngModel) {
            return;
        }

        if (this.isPeriodicidadeMensal()) { // Mensal (retorna 1 mês)
            this.mesAnterior();
        } else if (this.isPeriodicidadeTrimestral()) { // Trimestral (retorna um trimestre)
            this.trimestreAnterior();
        } else if (this.isPeriodicidadeSemestral()) { // Semestral (retorna um semestre)
            this.semestreAnterior();
        } else if (this.isPeriodicidadeAnual()) { // Anual (retorna 1 ano)
            this.anoAnterior();
        } else { // Diária (diminui 1 dia)
            this.diaAnterior();
        }
    }

    /** Retorna um dia */
    private diaAnterior() {
        const date = new Date(this.ngModel);
        date.setDate(date.getDate() - 1);
        this.datepicker?.datepicker('setDate', date);
        this.datepicker?.datepicker('update');
    }

    /** Retorna um mês */
    private mesAnterior() {
        const date = new Date(this.ngModel);
        date.setMonth(date.getMonth() - 1);
        this.datepicker?.datepicker('setDate', date);
        this.datepicker?.datepicker('update');
    }

    /** Retorna um trimestre */
    private trimestreAnterior() {
        switch (this.trimestre) {
            case 1: // Trimestre 1
                this.onChangeTrimestre(4, this.ngModel.getFullYear() - 1); // Vai para o trimestre 4 do ano anterior
                break;
            case 2: // Trimestre 2
                this.onChangeTrimestre(1, this.ngModel.getFullYear()); // Vai para o trimestre 1 do ano atual
                break;
            case 3: // Trimestre 3
                this.onChangeTrimestre(2, this.ngModel.getFullYear()); // Vai para o trimestre 2 do ano atual
                break;
            case 4: // Trimestre 4
                this.onChangeTrimestre(3, this.ngModel.getFullYear()); // Vai para o trimestre 3 do ano atual
                break;
            default:
                break;
        }
    }

    /** Retorna um semestre */
    private semestreAnterior() {
        switch (this.semestre) {
            case 1: // Semestre 1
                this.onChangeSemestre(2, this.ngModel.getFullYear() - 1); // Vai para o semestre 2 do ano anterior
                break;
            case 2: // Semestre 2
                this.onChangeSemestre(1, this.ngModel.getFullYear()); // Vai para o semestre 1 do ano atual
                break;
            default:
                break;
        }
    }

    /** Retorna um ano */
    private anoAnterior() {
        const date = new Date(this.ngModel);
        date.setFullYear(date.getFullYear() - 1);
        this.datepicker?.datepicker('setDate', date);
        this.datepicker?.datepicker('update');
    }

    /** Título do avanço da data com base no mode do datepicker */
    nextTitle() {
        if (!this.withNavigation || !this.ngModel) {
            return '';
        }

        if (this.isPeriodicidadeMensal()) { // Mensal
            return 'Próximo Mês';
        } else if (this.isPeriodicidadeTrimestral()) { // Trimestral
            return 'Próximo Trimestre';
        } else if (this.isPeriodicidadeSemestral()) { // Semestral
            return 'Próximo Semestre';
        } else if (this.isPeriodicidadeAnual()) { // Anual
            return 'Próximo Ano';
        } else { // Diária
            return 'Próximo Dia';
        }
    }

    /** Avança a data com base no mode do datepicker */
    next() {
        if (!this.withNavigation || !this.ngModel) {
            return;
        }

        if (this.isPeriodicidadeMensal()) { // Mensal (avança 1 mês)
            this.proximoMes();
        } else if (this.isPeriodicidadeTrimestral()) { // Trimestral (avança 1 trimestre)
            this.proximoTrimestre();
        } else if (this.isPeriodicidadeSemestral()) { // Semestral (avança 1 semestre)
            this.proximoSemestre();
        } else if (this.isPeriodicidadeAnual()) { // Anual (avança 1 ano)
            this.proximoAno();
        } else { // Diária (avança 1 dia)
            this.proximoDia();
        }
    }

    /** Avança um dia */
    private proximoDia() {
        const date = new Date(this.ngModel);
        date.setDate(date.getDate() + 1);
        this.datepicker?.datepicker('setDate', date);
        this.datepicker?.datepicker('update');
    }

    /** Avança um mês */
    private proximoMes() {
        const date = new Date(this.ngModel);
        date.setMonth(date.getMonth() + 1);
        this.datepicker?.datepicker('setDate', date);
        this.datepicker?.datepicker('update');
    }

    /** Avança um trimestre */
    private proximoTrimestre() {
        switch (this.trimestre) {
            case 1: // Trimestre 1
                this.onChangeTrimestre(2, this.ngModel.getFullYear()); // Vai para o trimestre 2 do ano atual
                break;
            case 2: // Trimestre 2
                this.onChangeTrimestre(3, this.ngModel.getFullYear()); // Vai para o trimestre 3 do ano atual
                break;
            case 3: // Trimestre 3
                this.onChangeTrimestre(4, this.ngModel.getFullYear()); // Vai para o trimestre 4 do ano atualthis.ngModel = new Date(this.ngModel.getFullYear(), 6, 1);
                break;
            case 4: // Trimestre 4
                this.onChangeTrimestre(1, this.ngModel.getFullYear() + 1); // Vai para o trimestre 1 do próximo ano
                break;
            default:
                break;
        }
    }

    /** Avança um semestre */
    private proximoSemestre() {
        switch (this.semestre) {
            case 1: // Semestre 1
                this.onChangeSemestre(2, this.ngModel.getFullYear()); // Vai para o semestre 2 do ano atual
                break;
            case 2: // Semestre 2
                this.onChangeSemestre(1, this.ngModel.getFullYear() + 1); // Vai para o semestre 1 do próximo ano
                break;
            default:
                break;
        }
    }

    /** Avança um ano */
    private proximoAno() {
        this.ngModel.setFullYear(this.ngModel.getFullYear() + 1);
        this.datepicker?.datepicker('setDate', this.ngModel);
        this.datepicker?.datepicker('update');
    }

    /** Adiciona a data informada ao array de datas desabilitadas */
    private addDisableDate(date: Date) {
        if (!date) {
            return;
        }
        if (!this.datesDisabled) {
            this.datesDisabled = [];
        }

        this.datesDisabled.push(date);
    }

    /** Seta as datas que devem ser desabilitadas no datepicker */
    private setDatesDisabled(sourceDate: Date): Promise<void> {
        return new Promise<void>(async (resolve) => {
            try {
                this.datesDisabled = [];
                const daysOfWeekDisabled: number[] = [];

                // Adicionando as datas que devem ser desabilitadas
                this.disableDates?.forEach(date => this.addDisableDate(date));

                // Desabilitando Domingo
                if (this.disableSunday) {
                    daysOfWeekDisabled.push(0);
                }
                // Desabilitando Sábado
                if (this.disableSaturday) {
                    daysOfWeekDisabled.push(6);
                }
                // Desabilitando Feriados
                if (this.disableFeriados) {
                    await this.searchFeriadosMes(sourceDate)
                            .then(feriados => {
                                feriados?.forEach(feriado => {
                                    if (!feriado.diaUtil) {
                                        this.addDisableDate(new Date(feriado.data));
                                    }
                                });
                            })
                            .catch();
                }

                // Desabilitando dias da semana
                this.datepicker?.datepicker('setDaysOfWeekDisabled', daysOfWeekDisabled);
                // Desabilitando datas que não podem ser selecionadas
                this.datepicker?.datepicker('setDatesDisabled', this.datesDisabled);

                resolve();
            } catch (error) {
                console.error(error);
            } finally {
                resolve();
            }
        });
    }

    /** Pesquisa os feriados do mês da data informada */
    private searchFeriadosMes(sourceDate: Date): Promise<Feriado[]> {
        return new Promise<Feriado[]>((resolve, reject) => {
            if (!sourceDate) {
                reject([]);
                return;
            }

            // Criando data inicial e final para filtrar somente feriados do mês informado
            const dataInicial = new Date(sourceDate.getFullYear(), sourceDate.getMonth(), 1);
            const dataFinal = new Date(dataInicial);
            dataFinal.setMonth(dataFinal.getMonth() + 1);
            dataFinal.setDate(dataFinal.getDate() - 1);
            // Criando o filtro com a data inicial e final do mês
            const filter = new FeriadoFilter(false);
            filter.dataInicial = dataInicial;
            filter.dataFinal = dataFinal;
            // Pesquisando os feriados do mês
            this.feriadoCrudHttpService.searchWithFilter(filter)
                .pipe(
                    take(1)
                )
                .subscribe(
                    feriados => resolve(feriados),
                    (() => reject([]))
                );
        });
    }

    /** Verifica se a data informada é uma data desabilitada */
    private isDateDisabled(date: Date): boolean {
        if (!this.datesDisabled || !date) {
            return false;
        }

        // Se for um domingo e domingo estiver desabilitado
        if (date.getDay() === 0 && this.disableSunday) {
            return true;
        }

        // Se for um sábado e sábado estiver desabilitado
        if (date.getDay() === 6 && this.disableSaturday) {
            return true;
        }

        // Verificando se a data é uma das datas que estão desabilitadas
        const index = this.datesDisabled.findIndex(dateDisabled => {
            // Se o dia, mês e ano forem iguais então é uma data que está desabilitada
            if (
                (dateDisabled.getDate() === date.getDate()) &&
                (dateDisabled.getMonth() === date.getMonth()) &&
                (dateDisabled.getFullYear() === date.getFullYear())
            ) {
                return true;
            }

            return false;
        });

        return index >= 0;
    }

    /** Remove o datepicker */
    private destroy() {
        this.datepicker?.datepicker('destroy');
        this.datepicker?.off('changeDate');
        this.datepicker?.off('changeMonth');
        this.datepicker?.off('show.bs.modal');
        this.datepicker?.off('shown.bs.modal');
        this.datepicker?.off('hide.bs.modal');
        this.datepicker?.off('hidden.bs.modal');
        this.datepicker = null;

        if (this.inputDate) {
            this.inputDate.nativeElement.value = '';
        }
    }

    ngOnDestroy() {
        this.destroy();
    }
}
